import { TFunction } from 'i18next';

import { projectStatusByCode, FullProject } from 'entities';

import { ListSorters } from 'components/ListManipulation';

import { getFirst } from 'utils/array';

export function getSorters(): ListSorters<FullProject>[] {
  const sorters: (ListSorters<FullProject> | null)[] = [
    {
      id: 'STATUS',

      default: true,

      getLabel: (t: TFunction) => t('status'),

      sorter: (a: FullProject, b: FullProject) => {
        const [aOrder, bOrder] = [a, b].map(({ status }) => projectStatusByCode[status].order);

        return aOrder - bOrder;
      },
    },

    {
      id: 'OWNER_NAME',
      getLabel: (t: TFunction) => t('owner-name'),
      sorter: (a: FullProject, b: FullProject) => {
        const [aOwnerName, bOwnerName] = [a, b].map(({ owner }) => owner.name);

        return aOwnerName.localeCompare(bOwnerName);
      },
    },

    {
      id: 'PROJECT_NAME',
      getLabel: (t: TFunction) => t('project-name'),
      sorter: (a: FullProject, b: FullProject) => {
        const [aProjectName, bProjectName] = [a, b].map(({ name }) => name);

        return aProjectName.localeCompare(bProjectName);
      },
    },

    {
      id: 'TICKET',
      getLabel: (t: TFunction) => t('freshdesk-ticket'),
      sorter: (a: FullProject, b: FullProject) => {
        const [aConverted, bConverted] = [a, b].map(({ ticket }) => Number(ticket));

        return aConverted - bConverted;
      },
    },

    {
      id: 'CLIENT_NAME',
      getLabel: (t: TFunction) => t('client-name'),
      sorter: (a: FullProject, b: FullProject) => {
        const [aClientName, bClientName] = [a, b].map(({ clientName }) => clientName);

        return aClientName.localeCompare(bClientName);
      },
    },

    {
      id: 'CLIENT_ORIGIN',
      getLabel: (t: TFunction) => t('client-origin'),
      sorter: (a: FullProject, b: FullProject) => {
        const [aClientOrigin, bClientOrigin] = [a, b].map(({ clientOrigin }) => clientOrigin);

        return aClientOrigin.localeCompare(bClientOrigin);
      },
    },

    {
      id: '#_OF_TASKS',
      getLabel: (t: TFunction) => t('amount-of-tasks'),
      sorter: (a: FullProject, b: FullProject) => {
        const [aLength, bLength] = [a, b].map(({ tasks }) => tasks.length);

        return bLength - aLength;
      },
    },

    {
      id: 'EXPECTED_DATE',
      getLabel: (t: TFunction) => t('expected-date'),
      sorter: (a: FullProject, b: FullProject) => {
        const defaultDate = new Date('2040-04-22T12:00:00.000').getTime();

        const [aDate, bDate] = [a, b].map(({ expectedDate }) =>
          expectedDate ? new Date(expectedDate as string).getTime() : defaultDate,
        );

        return aDate - bDate;
      },
    },

    {
      id: 'TAGS',
      getLabel: (t: TFunction) => t('tags'),
      sorter: (a: FullProject, b: FullProject) => {
        const [aLength, bLength] = [a, b].map(({ tags }) => tags.length);

        const lengthSorting = bLength - aLength;

        if (lengthSorting !== 0) return lengthSorting;

        const [aLowerName, bLowerName] = [a, b].map(({ tags }) => {
          const sortedTags = tags.slice().sort((aTag, bTag) => aTag.name.localeCompare(bTag.name));

          const lowerName = getFirst(sortedTags)?.name || 'ZZZZZ';

          return lowerName;
        });

        return aLowerName.localeCompare(bLowerName);
      },
    },
  ];

  return sorters.filter(Boolean) as ListSorters<FullProject>[];
}
